import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useLocation, Navigate, useNavigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { ClipboardList, CalendarDays, Receipt, Settings as SettingsIcon, BarChart3, Sparkles, ArrowLeft, Loader2 } from "lucide-react";
import Today from "./Today";
import CalendarPage from "./Calendar";
import Reception from "./Reception";
import SettingsPage from "./Settings";
import Stats from "./Stats";
import { hasToken, verifyToken, logout } from "../../lib/api";

const NAV = [
  { to: "/admin/pulizie/oggi",         label: "Oggi",         icon: ClipboardList, testId: "hk-nav-oggi" },
  { to: "/admin/pulizie/calendario",   label: "Calendario",   icon: CalendarDays,  testId: "hk-nav-calendario" },
  { to: "/admin/pulizie/reception",    label: "Reception",    icon: Receipt,       testId: "hk-nav-reception" },
  { to: "/admin/pulizie/statistiche",  label: "Statistiche",  icon: BarChart3,     testId: "hk-nav-stats" },
  { to: "/admin/pulizie/impostazioni", label: "Impostazioni", icon: SettingsIcon,  testId: "hk-nav-impostazioni" },
];

const TITLES = {
  oggi:         ["Gestione Pulizie", "Lavori del giorno"],
  calendario:   ["Calendario",        "Arrivi e partenze"],
  reception:    ["Reception",         "Addebiti e magazzino"],
  statistiche:  ["Statistiche",       "Trend del team"],
  impostazioni: ["Impostazioni",      "Camere e lavori"],
};

function Header({ title, subtitle }) {
  return (
    <div className="bg-[#3A1B18] text-[#F7EBDF] rounded-b-3xl px-4 pt-3 pb-6 shadow-md relative overflow-hidden">
      <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-[#C5A059]/10 pointer-events-none" />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2">
          <Link to="/admin" data-testid="hk-back-admin" className="h-8 w-8 rounded-full bg-[#F7EBDF]/15 border border-[#F7EBDF]/25 flex items-center justify-center hover:bg-[#F7EBDF]/25 transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="h-8 w-8 rounded-full bg-[#F7EBDF] flex items-center justify-center shadow-md shrink-0" data-testid="hk-header-logo">
            <Sparkles className="h-4 w-4 text-[#4A1C17]" />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-lg sm:text-xl font-bold tracking-wide leading-tight truncate" data-testid="hk-header-title">{title}</h1>
            <p className="text-sm text-[#C5A059] leading-tight italic truncate">{subtitle}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HousekeepingPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    (async () => {
      if (hasToken()) {
        const ok = await verifyToken();
        setAuthed(ok);
        if (!ok) logout();
      }
      setChecking(false);
    })();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }
  if (!authed) {
    // Bounce to /admin — the admin login form doubles for both.
    return <Navigate to="/admin" replace state={{ from: pathname }} />;
  }

  const seg = pathname.split("/").filter(Boolean)[2] || "oggi";
  const [title, subtitle] = TITLES[seg] || TITLES.oggi;

  return (
    <div className="min-h-screen bg-[#F7EBDF]">
      <Header title={title} subtitle={subtitle} />
      <main className="pb-28">
        <Routes>
          <Route index element={<Navigate to="oggi" replace />} />
          <Route path="oggi" element={<Today />} />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="reception" element={<Reception />} />
          <Route path="statistiche" element={<Stats />} />
          <Route path="impostazioni" element={<SettingsPage />} />
        </Routes>
      </main>
      <nav className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-[#E8DCC8] z-50 pb-[env(safe-area-inset-bottom)]" data-testid="hk-bottom-nav">
        <div className="max-w-2xl mx-auto flex justify-center gap-1 sm:gap-2 px-2 py-2">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex-1 min-w-0 max-w-[92px] flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-2xl text-[10px] sm:text-xs font-semibold transition-colors ${
                  isActive ? "text-[#4A1C17] bg-[#FAF3EE]" : "text-[#7C5A4E] hover:text-[#4A1C17]"
                }`
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="truncate w-full text-center leading-tight">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      <Toaster position="top-center" richColors />
    </div>
  );
}
